import React from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

// Register required Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BarChart = ({ data }) => {
    // Extract cities and their respective data
    const cities = Object.keys(data);
    // const keys = ["medicine_amount", "package_amount", "consultation_amount"];
    const keys = ["package_amount"];

    // Calculate scale factors dynamically for each key
    const scaleFactors = keys.reduce((acc, key) => {
        const maxValue = Math.max(...cities.map((city) => parseFloat(data[city][key] || 0)));
        acc[key] = maxValue > 1000 ? Math.pow(10, Math.floor(Math.log10(maxValue / 100))) : 1; // Dynamically scale down large values
        return acc;
    }, {});

    // Prepare datasets
    const datasets = keys.map((key, index) => ({
        label: key.replace("_", " ").toUpperCase(),
        data: cities.map((city) => {
            const value = parseFloat(data[city][key] || 0);
            return value / scaleFactors[key]; // Scale down based on the calculated factor
        }),
        // backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"][index], // Use different colors
        backgroundColor: ["#36A2EB"][index],
        barThickness: 15, // Reduce bar thickness
        borderRadius: 5, // Add border radius to bars
    }));

    // Chart.js configuration
    const chartData = {
        labels: cities, // X-axis labels
        datasets: datasets, // Datasets for bars
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false, // Allow custom height/width
        plugins: {
            legend: {
                position: "bottom", // Position of legend
            },
            title: {
                display: false,
                text: "City-wise Data Representation", // Title of the chart
            },
            tooltip: {
                callbacks: {
                    title: (tooltipItems) => {
                        // Show city name in the tooltip title
                        return `City: ${tooltipItems[0].label}`;
                    },
                    label: (context) => {
                        const datasetLabel = context.dataset.label.toLowerCase().replace(" ", "_");
                        const rawValue = context.raw;
                        const city = context.label; // Get the city name
                        const originalValue = rawValue * scaleFactors[datasetLabel]; // Scale back the value for tooltip

                        // Add specific information for each dataset
                        let countKey = ""; // To handle count for each type
                        let title = "";

                        if (datasetLabel === "package_amount") {
                            countKey = "package_count";
                            title = "Package";
                        } else if (datasetLabel === "medicine_amount") {
                            countKey = "medicine_count";
                            title = "Medicine";
                        } else if (datasetLabel === "consultation_amount") {
                            countKey = "consultation_count";
                            title = "Consultation";
                        }

                        // Get the count value
                        const countValue = parseFloat(data[city][countKey] || 0);

                        // Format the original value as per K, L, Cr
                        let formattedValue;
                        if (originalValue >= 10000000) formattedValue = `${(originalValue / 10000000).toFixed(1)} Cr`;
                        else if (originalValue >= 100000) formattedValue = `${(originalValue / 100000).toFixed(1)} L`;
                        else if (originalValue >= 1000) formattedValue = `${(originalValue / 1000).toFixed(1)} K`;
                        else formattedValue = originalValue;

                        // Return the formatted tooltip
                        return `${title}: ${formattedValue} | Count: ${countValue}`;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: false, // Ensure no stacking
                grid: {
                    display: false, // Remove vertical gridlines
                },
                barPercentage: 0.8, // Increase spacing between bars
                categoryPercentage: 0.6, // Adjust the width of groups
            },
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => {
                        return value; // Display scaled-down values on the y-axis
                    },
                },
            },
        },
    };

    return (
        <div style={{ height: "14rem", width: "100%" }}> {/* Set a fixed height */}
            <Bar data={chartData} options={chartOptions} height={300} /> {/* Adjust height */}
        </div>
    );
};

export default BarChart;
